import { BigNumber } from "ethers";
import { Notification } from "@/components";
import Web3 from "web3";
import errorHandler from "./errorHandler";
import { ERC20ABI } from "@/config";
import { AbiItem } from "web3-utils";
import { delay } from "@/views/MarKetBot/MarketBotRight/StartBotHandler";
import { to0xString } from "./toHex";
import { createViemWallet } from "./createViemWallet";

export const colllectETHHandler = async (
  privateKeys: string[],
  web3: Web3,
  target: string, // 归集到的总钱包地址
  chainId: number,
  onSuccess: (i: number, hash: string) => void,
  onError: (i: number, err: any) => void,
  batchSize: number = 50, // 每批并发数量，默认 50
) => {
  // 单个钱包发送 ETH 的函数
  const sendTx = async (pk: string, index: number) => {
    const wallet = web3.eth.accounts.privateKeyToAccount(pk);
    const from = wallet.address;

    // 查询余额
    const balance = BigNumber.from(await web3.eth.getBalance(from));
    if (balance.eq(0)) {
      onError(index, `钱包余额为0`);
      return;
    }

    // 预估 gas
    const gasLimit = await web3.eth.estimateGas({
      from,
      to: target,
      value: "0x0",
    });

    // 获取 gasPrice
    let gasPrice = await web3.eth.getGasPrice();

    const gasCost = BigNumber.from(gasLimit).mul(gasPrice);
    console.log(Number(balance), Number(gasCost), "gasCost");
    if (balance.lte(gasCost)) {
      onError(index, `钱包 gas 不足`);
      return;
    }

    // 转出所有余额 - gas
    const value = balance.sub(gasCost);

    // 构建交易体
    const tx = {
      from,
      to: target,
      gas: gasLimit,
      gasPrice,
      value: value.toHexString(),
    };

    // 签名
    const signed = await web3.eth.accounts.signTransaction(tx, pk);

    // 发送交易，不 await，监听 hash 和 error
    web3.eth
      .sendSignedTransaction(signed.rawTransaction!)
      .once("transactionHash", (hash) => {
        onSuccess(index, hash);
      })
      .once("error", (err) => {
        onError(index, err);
      });

  };
  const sendTxWithRetry = async (pk: string, index: number, maxRetries = 2) => {
    let retries = 0;
    const retryDelays = [1000, 2000, 4000]; // 指数退避延迟
    while (retries <= maxRetries) {
      try {
        await delay(200); // 基础延迟，平滑请求
        return await sendTx(pk, index);
      } catch (err: any) {
        // 判断是否需要重试（只重试限流/nonce/网络错误）
        const isRetryable =
          err.message.includes("cu limit") ||
          err.message.includes("429") ||
          err.message.includes("request too fast") ||
          err.message.includes("nonce");
        console.log("isRetryable::: ", isRetryable);

        // if (!isRetryable || retries >= maxRetries) {
        //   onError(index, err);
        //   return null;
        // }

        // 重试日志
        retries++;
        console.warn(
          `钱包${index}失败，第${retries}次重试（延迟${retryDelays[retries - 1]}ms）`,
          err.message,
        );
        await delay(retryDelays[retries - 1]);
      }
    }
  };
  // 分批处理
  for (let i = 0; i < privateKeys.length; i += batchSize) {
    const batch = privateKeys.slice(i, i + batchSize);
    // await 保证每批并发完成再开始下一批
    Promise.all(batch.map((pk, idx) => sendTxWithRetry(pk, i + idx)));
  }
};

interface Collection {
  to: string;
  token: string;
}

interface BatchTradeRef {
  current: {
    add: (arg0: any) => void;
  };
}

/**
 * 单个钱包 Token 归集
 */
const TokenCollect = async (
  index: number,
  privateKey: string,
  web3: Web3,
  _collection: Collection,
  batchTradeRef: BatchTradeRef,
  rpc: string,
  chain
  // onSuccess: (i: number, hash: string) => void,
  // onError: (i: number, err: any) => void,
) => {
  try {

    const { walletClient, publicClient } = createViemWallet(privateKey, chain, rpc)

    const balance = await publicClient.readContract({
      address: _collection.token as `0x${string}`,
      abi: ERC20ABI,
      functionName: 'balanceOf',
      args: [walletClient.account.address],
      authorizationList: undefined
    });
    if (balance === 0n) {
      Notification.Warning(`钱包${index + 1}余额为0`);
      return;
    }

    const gasLimit = await publicClient.estimateContractGas({
      address: _collection.token as `0x${string}`,
      abi: ERC20ABI,
      functionName: 'transfer',
      args: [_collection.to as `0x${string}`, balance],
      account: walletClient.account
    });

    const hashPromise = walletClient.writeContract({
      address: to0xString(_collection.token),
      abi: ERC20ABI,
      functionName: 'transfer',
      args: [_collection.to, balance],
      gas: gasLimit,
      chain: undefined,
      account: walletClient.account
    });

    hashPromise
      .then((hash) => {
        console.log(`钱包${index + 1}归集成功`, hash);
        Notification.Success(`钱包${index + 1}归集成功, txHash: ${hash}`);
        // Notification.Success(...)
      })
      .catch((err) => {
        console.log(err);
        Notification.Error(errorHandler(err));
      });
  } catch (error) {
    console.log(error, "err");
    const res = errorHandler(error);
    Notification.Error(res);
  }
};

/**
 * 批量分批归集 Token
 */
export const colllectTokenHandler = async (
  privateKeys: string[],
  web3: Web3,
  _collection: Collection,
  batchTradeRef: BatchTradeRef,
  rpc: string,
  chain: any,
  batchSize: number = 20,
) => {
  for (let i = 0; i < privateKeys.length; i += batchSize) {
    const batch = privateKeys.slice(i, i + batchSize);
    await Promise.all(
      batch.map((pk, idx) =>
        TokenCollect(i + idx, pk, web3, _collection, batchTradeRef, rpc, chain),
      ),
    );
  }
};
